/*
 * WW Benefit Demo — motion for the "Work at the speed of thought" section
 * Author: Plaud Tayler — 2026-09-02
 * Page: Figma node 1761:21905; motion ported from Section2_V2_NotMVP.html
 * Version: 1.1.0 - 2026-09-17 19:40 (UTC+8) - Plaud Tayler — the status tag's state machine is removed with the tag
 * Version-note: 1.0.0 - 2026-09-02 18:35 (UTC+8) - Plaud Tayler — initial port
 *
 * The loops are the prototype's, near enough line for line: the canvas waveform, the running
 * line, the transcript typewriter, and the request → reply → checklist sequence on the right.
 * The prototype's fifth, the status tag's state machine, was removed with the tag itself on
 * 2026-09-17 — markup, stylesheet region and `state` block went at the same time. What is NOT the prototype's is the lifecycle around
 * them, and that is deliberate:
 *
 *  - The prototype starts every loop at parse time and never stops. Measured on the prototype
 *    itself: 6,897 canvas fills per 2s with the section in view, and 6,840 per 2s with it
 *    scrolled 5,000px away — i.e. no reduction at all. On a 14,900px page that runs for the whole
 *    session. Here an IntersectionObserver owns play/pause.
 *  - The prototype reads prefers-reduced-motion but only flattens the *content* of each frame;
 *    `frame()` still calls requestAnimationFrame unconditionally, so under reduce the motion
 *    stops and the cost stays. Here reduce means the loops never start and the section renders
 *    its finished state.
 *  - Every id in the prototype is global (20 of them, driven by 16 getElementById calls). A
 *    Shopify section can be duplicated in the theme editor, so all lookups are scoped to this
 *    element and the injected SVG gradient ids are suffixed per instance.
 *
 * Copy is not in here. Every string the visitor reads comes from the markup the section rendered
 * from its settings; this file only reads it back out and types it in.
 */

(function () {
  'use strict';

  if (customElements.get('ww-benefit-demo')) return;

  /* The waveform silhouette from the comp: 57 bars, drawn in a fixed 290x60 design space that is
     mapped onto whatever the card currently measures. */
  var BASE = [25, 23, 24, 25, 21, 21, 21, 16, 16, 13, 13, 15, 13, 15, 14, 10, 12, 15, 14, 17, 16,
              19, 20, 17, 14, 14, 11, 11, 10, 5, 4, 4, 4, 4, 4, 4, 5, 8, 10, 13, 29, 34, 24, 19,
              14, 9, 42, 49, 45, 45, 15, 19, 18, 18, 18, 13, 16];
  var X0 = 2.6, PITCH = 5.058, BW = 2, MID = 30, DESIGN_W = 290, DESIGN_H = 60;

  var WwBenefitDemo = function () {
    return Reflect.construct(HTMLElement, [], WwBenefitDemo);
  };
  WwBenefitDemo.prototype = Object.create(HTMLElement.prototype);
  WwBenefitDemo.prototype.constructor = WwBenefitDemo;
  Object.setPrototypeOf(WwBenefitDemo, HTMLElement);

  var P = WwBenefitDemo.prototype;

  P.connectedCallback = function () {
    if (this.bound) return;
    this.bound = true;

    this.uid = this.getAttribute('data-uid') || String(Math.floor(Date.now() % 1e6));
    this.gen = 0;
    this.timers = [];
    this.rafs = [];
    this.waveEnergy = 1;
    this.playing = false;

    this.q = function (sel) { return this.querySelector(sel); }.bind(this);
    this.qa = function (sel) { return Array.prototype.slice.call(this.querySelectorAll(sel)); }.bind(this);

    this.track = this.q('[data-wbd-track]');
    this.canvas = this.q('[data-wbd-wave]');
    this.entries = this.qa('[data-wbd-entry]');
    this.promptEl = this.q('[data-wbd-prompt]');
    this.pillEl = this.q('[data-wbd-pill]');
    this.cardEl = this.q('[data-wbd-card]');
    this.rails = this.qa('[data-wbd-rail]');
    this.steps = this.qa('[data-wbd-step]').map(function (el) {
      return { el: el, label: el.querySelector('[data-wbd-steplabel]'), text: (el.querySelector('[data-wbd-txt]') || {}).textContent || '' };
    });

    this.fullEntries = this.entries.map(function (e) {
      var t = e.querySelector('[data-wbd-txt]');
      return t ? t.textContent : '';
    });
    this.promptText = this.textOf(this.promptEl);
    this.pillText = this.textOf(this.pillEl);

    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onMotionChange = this.handleMotionChange.bind(this);
    if (this.motionQuery.addEventListener) this.motionQuery.addEventListener('change', this.onMotionChange);
    else if (this.motionQuery.addListener) this.motionQuery.addListener(this.onMotionChange);

    this.setupCanvas();

    if (this.motionQuery.matches) { this.renderStatic(); return; }

    /* The whole point of the observer: off-screen this section costs nothing. rootMargin gives
       the sequence a moment to be underway by the time it is actually looked at. */
    this.io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) this.play(); else this.pause();
      }
    }.bind(this), { rootMargin: '200px 0px' });
    this.io.observe(this);

    /* A tab in the background gets the same treatment as a section out of view. */
    this.onVisibility = function () {
      if (document.hidden) this.pause();
      else if (this.inView) this.play();
    }.bind(this);
    document.addEventListener('visibilitychange', this.onVisibility);
  };

  P.disconnectedCallback = function () {
    this.pause();
    if (this.io) { this.io.disconnect(); this.io = null; }
    if (this.ro) { this.ro.disconnect(); this.ro = null; }
    if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    if (this.motionQuery) {
      if (this.motionQuery.removeEventListener) this.motionQuery.removeEventListener('change', this.onMotionChange);
      else if (this.motionQuery.removeListener) this.motionQuery.removeListener(this.onMotionChange);
    }
    this.bound = false;
  };

  P.handleMotionChange = function () {
    if (this.motionQuery.matches) { this.pause(); this.renderStatic(); }
  };

  P.textOf = function (el) {
    if (!el) return '';
    var t = el.querySelector('[data-wbd-txt]');
    return t ? t.textContent : '';
  };

  /* ---- scheduling primitives that pause cleanly ------------------------------------------ */

  P.wait = function (ms, my) {
    return new Promise(function (resolve) {
      var id = setTimeout(function () { resolve(my === this.gen); }.bind(this), ms);
      this.timers.push(id);
    }.bind(this));
  };

  P.raf = function (fn) {
    var id = requestAnimationFrame(fn);
    this.rafs.push(id);
    return id;
  };

  P.clearAll = function () {
    this.timers.forEach(clearTimeout); this.timers = [];
    this.rafs.forEach(cancelAnimationFrame); this.rafs = [];
  };

  /* ---- canvas ---------------------------------------------------------------------------- */

  P.setupCanvas = function () {
    if (!this.canvas) return;
    this.ctx = this.canvas.getContext('2d');
    this.phase = BASE.map(function (_, i) { return i * 0.34; });
    this.sizeCanvas();
    if (typeof ResizeObserver === 'function') {
      this.ro = new ResizeObserver(function () { this.sizeCanvas(); this.drawWave(this.lastT || 0); }.bind(this));
      this.ro.observe(this.canvas);
    }
  };

  /* The design space stays 290x60 whatever the card measures, so the silhouette constants above
     never have to be rewritten for a breakpoint. */
  P.sizeCanvas = function () {
    if (!this.canvas || !this.ctx) return;
    var r = this.canvas.getBoundingClientRect();
    if (!r.width || !r.height) return;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(r.width * dpr);
    this.canvas.height = Math.round(r.height * dpr);
    this.ctx.setTransform(this.canvas.width / DESIGN_W, 0, 0, this.canvas.height / DESIGN_H, 0, 0);
  };

  P.drawWave = function (t, flat) {
    if (!this.ctx) return;
    this.lastT = t;
    this.ctx.clearRect(0, 0, DESIGN_W, DESIGN_H);
    this.ctx.fillStyle = '#9c9ca2';
    var sum = 0;
    /* speech cadence: roughly four syllables a second riding a slower breath envelope */
    var sweep = ((t / 4200) % 1) * 1.24 - 0.12;
    var env = 0.96 + 0.04 * Math.sin(t / 900 + 0.7);
    for (var i = 0; i < BASE.length; i++) {
      var u = i / (BASE.length - 1);
      var live = flat ? 1
        : env * (0.84 + 0.24 * (0.5 + 0.5 * Math.sin(t / 320 + this.phase[i]))
                      + 0.05 * Math.sin(t / 140 + this.phase[i] * 0.7));
      var near = Math.exp(-Math.pow((u - sweep) / 0.17, 2));
      var h = Math.max(3, BASE[i] * live * (1 + (flat ? 0 : 0.24 * near)));
      sum += live;
      this.roundedBar(X0 + i * PITCH - BW / 2, MID - h / 2, BW, h);
    }
    this.waveEnergy = sum / BASE.length;
  };

  /* ctx.roundRect only landed in Safari 16.4, and this section is not worth losing on an older
     iPad, so the bar is drawn from arcs instead. */
  P.roundedBar = function (x, y, w, h, r) {
    var ctx = this.ctx;
    r = Math.min(w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
    ctx.fill();
  };

  /* ---- running line ---------------------------------------------------------------------- */

  P.measureTicker = function () {
    if (!this.track || !this.track.children.length) return;
    this.unitLen = this.track.children[0].getBoundingClientRect().width;
    this.offset = -this.unitLen;
    this.track.style.transform = 'translateX(' + this.offset + 'px)';
    this.paintGradient();
  };

  P.paintGradient = function () {
    var kids = this.track ? this.track.children : [];
    for (var c = 0; c < kids.length; c++) {
      kids[c].style.backgroundPositionX = (-(this.offset + c * this.unitLen)) + 'px';
    }
  };

  /* ---- transcript ------------------------------------------------------------------------ */

  P.lockHeights = function () {
    this.entries.forEach(function (e) {
      var body = e.querySelector('[data-wbd-body]');
      if (body && !body.style.height) body.style.height = body.offsetHeight + 'px';
      var txt = e.querySelector('[data-wbd-txt]');
      if (txt) txt.textContent = '';
      var meta = e.querySelector('[data-wbd-meta]');
      if (meta) meta.style.opacity = '0';
    });
  };

  /* Characters land in bursts — fast while the wave is loud, almost stalled in its dips — which
     is what makes it read as transcription rather than as a typewriter. */
  P.transcribe = function (entry, text, my) {
    var self = this;
    return new Promise(function (resolve) {
      var txt = entry.querySelector('[data-wbd-txt]');
      var caret = entry.querySelector('[data-wbd-tcaret]');
      if (!txt) return resolve(true);
      if (caret) caret.classList.remove('is-off');
      var i = 0, credit = 0, prev = performance.now();
      (function tick(now) {
        if (my !== self.gen) return resolve(false);
        var dt = Math.min(90, now - prev); prev = now;
        var loud = Math.max(0, Math.min(1, (self.waveEnergy - 0.88) / 0.2));
        credit += (18 + 52 * loud) * dt / 1000;
        while (credit >= 1 && i < text.length) { credit -= 1; i++; }
        txt.textContent = text.slice(0, i);
        if (i < text.length) self.raf(tick);
        else { if (caret) caret.classList.add('is-off'); resolve(true); }
      })(prev);
    });
  };

  P.showMeta = function (entry, instant) {
    var m = entry.querySelector('[data-wbd-meta]');
    if (!m) return;
    if (instant) {
      m.style.transition = 'none';
      m.style.opacity = '1';
      this.raf(function () { m.style.transition = ''; });
    } else {
      m.style.opacity = '1';
    }
  };

  P.transcriptLoop = async function (my) {
    var first = true;
    for (;;) {
      if (my !== this.gen) return;
      this.entries.forEach(function (e) {
        var t = e.querySelector('[data-wbd-txt]'); if (t) t.textContent = '';
        var m = e.querySelector('[data-wbd-meta]'); if (m) m.style.opacity = '0';
        var b = e.querySelector('[data-wbd-body]'); if (b) b.style.opacity = '1';
      });
      if (!first && !(await this.wait(250, my))) return;
      this.showMeta(this.entries[0], first);
      if (!first && !(await this.wait(180, my))) return;
      if (!(await this.transcribe(this.entries[0], this.fullEntries[0], my))) return;
      first = false;
      if (!(await this.wait(450, my))) return;
      if (this.entries[1]) {
        this.showMeta(this.entries[1], false);
        if (!(await this.wait(220, my))) return;
        if (!(await this.transcribe(this.entries[1], this.fullEntries[1], my))) return;
      }
      if (!(await this.wait(1400, my))) return;
      this.entries.forEach(function (e) {
        var b = e.querySelector('[data-wbd-body]');
        if (b) { b.style.transition = 'opacity .4s ease'; b.style.opacity = '0'; }
        var m = e.querySelector('[data-wbd-meta]'); if (m) m.style.opacity = '0';
      });
      if (!(await this.wait(480, my))) return;
    }
  };

  /* ---- request, reply, checklist ---------------------------------------------------------- */

  P.reveal = function (el, on) {
    if (!el) return;
    el.style.transition = 'opacity .45s ease, transform .45s cubic-bezier(.2,.8,.25,1)';
    el.style.opacity = on ? '1' : '0';
    el.style.transform = on ? 'translateY(0)' : 'translateY(8px)';
  };

  P.typeSteady = function (el, text, cps, my) {
    var self = this;
    return new Promise(function (resolve) {
      var txt = el && el.querySelector('[data-wbd-txt]');
      var caret = el && el.querySelector('[data-wbd-tcaret]');
      if (!txt) return resolve(true);
      if (caret) caret.classList.remove('is-off');
      var i = 0, step = 1000 / cps, acc = 0, prev = performance.now();
      (function tick(now) {
        if (my !== self.gen) return resolve(false);
        acc += now - prev; prev = now;
        while (acc >= step && i < text.length) { acc -= step; i++; }
        txt.textContent = text.slice(0, i);
        if (i < text.length) self.raf(tick);
        else { if (caret) caret.classList.add('is-off'); resolve(true); }
      })(prev);
    });
  };

  P.clearRight = function () {
    this.reveal(this.promptEl, false);
    this.reveal(this.pillEl, false);
    this.reveal(this.cardEl, false);
    [this.promptEl, this.pillEl].forEach(function (el) {
      if (!el) return;
      var t = el.querySelector('[data-wbd-txt]'); if (t) t.textContent = '';
      var c = el.querySelector('[data-wbd-tcaret]'); if (c) c.classList.add('is-off');
    });
    this.steps.forEach(function (s) {
      s.el.classList.remove('is-in');
      s.el.classList.remove('is-settled');
      s.el.style.opacity = '0';
      var t = s.el.querySelector('[data-wbd-txt]'); if (t) t.textContent = '';
      var c = s.el.querySelector('[data-wbd-tcaret]'); if (c) c.classList.add('is-off');
    });
    this.rails.forEach(function (r) { r.style.height = '0'; });
  };

  P.rightPass = async function (my) {
    var ok = true;
    var step = async function (ms) { ok = await this.wait(ms, my); return ok; }.bind(this);

    if (!(await step(120))) return;
    this.reveal(this.promptEl, true);
    if (!(await step(300))) return;
    if (!(await this.typeSteady(this.promptEl, this.promptText, 30, my))) return;
    if (!(await step(550))) return;
    this.reveal(this.pillEl, true);
    if (!(await step(260))) return;
    if (!(await this.typeSteady(this.pillEl, this.pillText, 30, my))) return;
    if (!(await step(340))) return;
    this.reveal(this.cardEl, true);
    if (!(await step(420))) return;

    var s = this.steps;
    if (!s.length) return;
    /* the checklist works through itself, one step at a time */
    s[0].el.classList.add('is-in'); s[0].el.style.opacity = '1';
    if (!(await this.typeSteady(s[0].label, s[0].text, 32, my))) return;
    if (!(await step(180))) return;
    if (this.rails[0]) this.rails[0].style.height = '12%';
    if (s[1]) { s[1].el.style.opacity = '1'; if (!(await this.typeSteady(s[1].label, s[1].text, 32, my))) return; }
    if (!(await step(140))) return;
    if (this.rails[0]) this.rails[0].style.height = '24%';
    if (s[2]) { s[2].el.style.opacity = '1'; if (!(await this.typeSteady(s[2].label, s[2].text, 32, my))) return; }
    if (!(await step(280))) return;
    if (s[3]) {
      s[3].el.classList.add('is-in'); s[3].el.style.opacity = '1';
      if (!(await this.typeSteady(s[3].label, s[3].text, 32, my))) return;
    }
    if (!(await step(200))) return;
    if (this.rails[1]) this.rails[1].style.height = '9%';
    if (!(await step(420))) return;
    s[0].el.classList.add('is-settled');
    if (s[3]) s[3].el.classList.add('is-settled');
    if (s[4]) {
      s[4].el.classList.add('is-in'); s[4].el.style.opacity = '1';
      await this.typeSteady(s[4].label, s[4].text, 24, my);
    }
  };

  P.rightLoop = async function (my) {
    for (;;) {
      if (my !== this.gen) return;
      this.clearRight();
      await this.rightPass(my);
      if (my !== this.gen) return;
      if (!(await this.wait(2400, my))) return;
      this.reveal(this.promptEl, false);
      this.reveal(this.pillEl, false);
      this.reveal(this.cardEl, false);
      if (!(await this.wait(700, my))) return;
    }
  };

  /* ---- play / pause ----------------------------------------------------------------------- */

  P.play = function () {
    this.inView = true;
    if (this.playing || this.motionQuery.matches || document.hidden) return;
    this.playing = true;
    var my = ++this.gen;

    this.sizeCanvas();
    this.measureTicker();
    this.lockHeights();

    var self = this, lastFrame = 0;
    (function frame(now) {
      if (my !== self.gen) return;
      self.drawWave(now || 0);
      if (self.unitLen) {
        if (!lastFrame) lastFrame = now;
        var dt = Math.min(80, now - lastFrame); lastFrame = now;
        self.offset += 38 * dt / 1000;               /* px per second, travelling right */
        if (self.offset >= 0) self.offset -= self.unitLen;
        self.track.style.transform = 'translateX(' + self.offset + 'px)';
        self.paintGradient();
      }
      self.raf(frame);
    })(performance.now());

    this.transcriptLoop(my);
    this.rightLoop(my);
  };

  P.pause = function () {
    this.inView = false;
    if (!this.playing) return;
    this.playing = false;
    this.gen++;            /* every in-flight sequence checks this and bails */
    this.clearAll();
  };

  /* Reduced motion: no loops at all, and the section shows the state the sequence ends on. */
  P.renderStatic = function () {
    this.gen++;
    this.clearAll();
    this.entries.forEach(function (e, i) {
      var t = e.querySelector('[data-wbd-txt]');
      if (t) t.textContent = this.fullEntries[i] || '';
      var m = e.querySelector('[data-wbd-meta]'); if (m) m.style.opacity = '1';
    }.bind(this));
    [this.promptEl, this.pillEl, this.cardEl].forEach(function (el) {
      if (!el) return;
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
    var pt = this.promptEl && this.promptEl.querySelector('[data-wbd-txt]');
    if (pt) pt.textContent = this.promptText;
    var lt = this.pillEl && this.pillEl.querySelector('[data-wbd-txt]');
    if (lt) lt.textContent = this.pillText;
    this.steps.forEach(function (s) {
      s.el.style.opacity = '1';
      s.el.classList.add('is-in', 'is-settled');
      var t = s.el.querySelector('[data-wbd-txt]'); if (t) t.textContent = s.text;
    });
    if (this.rails[0]) this.rails[0].style.height = '24%';
    if (this.rails[1]) this.rails[1].style.height = '9%';
    this.measureTicker();
    this.drawWave(0, true);
  };

  customElements.define('ww-benefit-demo', WwBenefitDemo);
})();
